import React, { useState, useMemo } from 'react';
import { Box, ThemeProvider, Fade, Container } from '@mui/material';
import DocumentsTable from '../components/DocumentsTable';
import { useDiaryItems, DiaryItem } from '../hooks/useDiaryItems';
import HeaderBar, { CurrentPages } from '../header-bar/HeaderBar';
import DocumentChat from '../components/DocumentChat';
import PageHeader from '../components/PageHeader';
import GradientBackground from '../resources/components/GradientBackground';
import theme from '../resources/NodeTheme';
import { useTranslation } from 'react-i18next';

// Helper function to get the primary date for a document
const getDocumentDate = (doc: DiaryItem): number => {
  const date = doc.paymentDate || doc.endDate || doc.startDate || doc.createdAt || doc.created_at;
  if (typeof date === 'string') {
    return new Date(date).getTime();
  }
  return date || 0;
};

// Helper function to get date-sorted documents with threads
const getDateSortedDocumentsWithThreads = (diaryItems: DiaryItem[]): DiaryItem[] => {
  return diaryItems
    .filter(doc => doc.threadId)
    .sort((a, b) => getDocumentDate(b) - getDocumentDate(a)); // Sort by date descending (newest first)
};

const DocumentsPage = () => {
  const { t } = useTranslation();
  const { diaryItems, refetch, isRefetching, isLoading } = useDiaryItems();
  const [selectedDocument, setSelectedDocument] = useState<DiaryItem | null>(null);
  const [drawerOpen, setDrawerOpen] = useState(false);

  // Get date-sorted documents with threads
  const dateSortedDocumentsWithThreads = useMemo(() => 
    getDateSortedDocumentsWithThreads(diaryItems), 
    [diaryItems]
  );
  
  // Find current index in the date-sorted array
  const currentIndex = selectedDocument 
    ? dateSortedDocumentsWithThreads.findIndex(doc => doc.threadId === selectedDocument.threadId)
    : -1;

  const handleRowClick = (document: DiaryItem) => {
    if (!document.threadId) {
      console.log('Document has no threadId, not opening chat');
      return;
    }
    
    setSelectedDocument(document);
    setDrawerOpen(true);
  };

  const handlePreviousChat = () => {
    if (currentIndex > 0) {
      const previousDoc = dateSortedDocumentsWithThreads[currentIndex - 1];
      setSelectedDocument(previousDoc);
    }
  };

  const handleNextChat = () => {
    if (currentIndex < dateSortedDocumentsWithThreads.length - 1) {
      const nextDoc = dateSortedDocumentsWithThreads[currentIndex + 1];
      setSelectedDocument(nextDoc);
    }
  };

  const handleClose = () => {
    setDrawerOpen(false);
    setSelectedDocument(null);
  };

  return (
    <ThemeProvider theme={theme}>
      <GradientBackground sx={{ minHeight: '100vh' }}>
        <Box sx={{ display: 'flex', flexDirection: 'column', width: '100%', height: '100%' }}>
          <HeaderBar
            currentPage={CurrentPages.Documents}
            setCurrentPage={() => {}}
          />
          <Fade in={!isLoading} timeout={500}>
            <Box sx={{ 
              flex: 1, 
            }}>
              <Box sx={{
                paddingTop: { xs: 2, sm: 3 },
                paddingLeft: { xs: 2, sm: 3 },
                paddingRight: { xs: 2, sm: 3 },
                paddingBottom: 0,
              }}>
                <PageHeader title={t('Activity')} />
              </Box>
              <DocumentsTable
                documents={diaryItems}
                onRowClick={handleRowClick}
                refetch={refetch}
                isRefetching={isRefetching}
                isLoading={isLoading}
              />
            </Box>
          </Fade>
        </Box>

        <DocumentChat
          threadId={selectedDocument?.threadId}
          onClose={handleClose}
          open={drawerOpen && !!selectedDocument?.threadId}
          title={selectedDocument?.title}
          onPreviousChat={handlePreviousChat}
          onNextChat={handleNextChat}
          hasPreviousChat={currentIndex > 0}
          hasNextChat={currentIndex < dateSortedDocumentsWithThreads.length - 1}
        />
      </GradientBackground>
    </ThemeProvider>
  );
};

export default DocumentsPage;
